import { getMessage } from 'util/locale';
import wam from '@tcc/wam';
import { log, postError } from 'util/logging';

import { errorLevel, errorDisplayTypes } from 'constants/errors';

// Selectors
import { getSupportInfo, isSupportInfoForAccountCountryAvailable } from 'user/selectors';

// Action Creators
import { addErrorToast, addModalMessage } from '../messages/index';
import { setFatalError } from 'current';

const errorQueue=[];
const haveHandledError={};
const stash = {};

const _getDefaultErrorMessageKey = () => {
  const withSupportInfo = stash.getState && isSupportInfoForAccountCountryAvailable(stash.getState());
  return withSupportInfo ? 'help-text__failure--load--field-data'
    : 'help-text__failure--load--field-data--unknown-support';
};

export const getErrorQueue = () => errorQueue;
export const addToErrorQueue = ( error ) => errorQueue.push(error);
export const clearErrorQueue = () => { errorQueue.length = 0; };

export const initErrorHandlers = ({ dispatch, getState }) => {
  // Rather than jump through hoops to ensure we have these
  // to pass every time we have an error, we'll cache them
  stash.dispatch = dispatch;
  stash.getState = getState;
};

export const handleErrorsInQueue = ({ convertErrorTo=null }={}) => {
  let worstError;
  errorQueue.forEach( (error) => {
    // Only report on errors we've not reported on

    const errorUuid = `${error.message}_${error.timestamp}_${error.severity}`;
    if( !haveHandledError[errorUuid] ) {
      postError(error); // log all errors, and let console .log use sourcemaps
      haveHandledError[errorUuid] = error;

      if( !worstError || error.severity > worstError.severity ) {
        worstError = error;
      }
    }
  });

  clearErrorQueue();
  if( !worstError ) {
    return convertErrorTo;
  }

  /* Re getSupportInfo() below: It may be the case that the specific message key from the error does not
     include support phone#/email, but in practice, all or most of them at BLOCK/WARN level should, and it is benign to
     pass the unused params. The reason they are not just added by the caller is that the error is added from
     places where Redux state is not available, which is needed to look up support info. */
  switch(worstError.severity) {
    case errorLevel.WARN: // Message user in a toast or modal
      switch(worstError.displayErrorAs) {
        case errorDisplayTypes.MODAL:
          stash.dispatch(addModalMessage({
            bodyHtmlMessageKey: worstError.messageKey,
            bodyHtmlMessageParams: getSupportInfo(stash.getState()),
            provideCloseButton: true }));
          break;
        default:
          stash.dispatch(addErrorToast(getMessage(worstError.messageKey, getSupportInfo(stash.getState()))));
          break;
      }
      break;
    case errorLevel.BLOCK: // Message user in a blocking fashion
      stash.dispatch(setFatalError({
        ...worstError,
        // @todo CFVWEB-2864 Need to get the plumbing right to send an HTML message to handle case
        // of message containing a support link.
        message: getMessage(worstError.messageKey, getSupportInfo(stash.getState())),
        actions: worstError.actions || [{
          textKey: 'button-text__reload-page',
          action: () => { window.location = 'https://climate.com/static/field-view/#/variable-rate/'; }
        }],
      }));
      break;
    case errorLevel.REAUTHENTICATE: // Bounce to auth
      log('Reauthenticating');
      wam.reAuthenticate();
      break;
    default:
      break;
  }
  return convertErrorTo; // make it no longer trip up anythen down the chain
};


// Wrap errors in our object to track
export const makeError = ({
  actions,
  context = {}, // optional freeform info provided by application code.
  displayErrorAs,
  error={ message: 'empty error triggered' },
  severity=errorLevel.BLOCK, // Don't rely on this default, it's used to trap the unexpected
  message,
  messageKey
}={}) => {
  const stack = error.stack || new Error().stack;
  return {
    actions,
    context,
    originalError: error,
    isPassed: true,
    severity,
    displayErrorAs: displayErrorAs || errorDisplayTypes.TOAST,
    message: message || error.message,
    messageKey: messageKey || _getDefaultErrorMessageKey(),
    timestamp: new Date().toISOString(),
    stack // Does not apply source-maps
  };
};

// Call this with config in a catch clause - it will provide the handler
/**
 * Creates an error handler function that processes and manages errors based on specified parameters
 * @param {Object} options - The configuration options for error handling
 * @param {Array} [options.actions] - Button actions to include in error modal
 * @param {string} [options.displayErrorAs] - Display UI for error ('modal' or 'toast')
 * @param {number} options.severity - Severity level of the error (see constants/errors.js)
 * @param {string} [options.message] - Internal information text about the error
 * @param {string} options.messageKey - i18n key for user-facing error message
 * @param {boolean} [options.andUpgradeIt=false] - Whether to upgrade the error severity to current level
 * @param {boolean} [options.andStopHere] - Whether to stop error propagation (defaults based on severity)
 * @param {boolean} options.andHandleNow - Whether to handle queued errors immediately
 * @param {*} [options.convertErrorTo=null] - Value to return if error is converted to response
 * @returns {Function} Error handler function that processes errors according to specified parameters
 * @throws {Error} Throws the processed error if andStopHere is false
 */
export const treatErrorAs = ({
  actions,
  displayErrorAs,
  severity,
  message,
  messageKey,
  andUpgradeIt=false,
  andStopHere,
  andHandleNow,
  convertErrorTo=null
}) => {
  return (error) => {
    console.error(error);
    if( !error ) {
      // An empty reject
      error = makeError({ error: 'No error given (reject()?)', displayErrorAs, message, messageKey, severity, actions});
    } else if (!error.isPassed ) {
      // Not our error, so we wrap it with extra data
      error = makeError({ displayErrorAs, error, message, messageKey, severity, actions});
    } else if( andUpgradeIt && error.severity < severity ) {
      // something threw a minor error, but it's worse in this spot
      error = makeError({ displayErrorAs, error, message, messageKey, severity, actions});
    }
    addToErrorQueue(error);

    if(andStopHere === undefined) {
      andStopHere = severity <= errorLevel.WARN; // Default to not interrupting if we intend to let the user work
    }

    if(andHandleNow === undefined) {
      andHandleNow = severity >= errorLevel.BLOCK; // Default to reacting to a blocking error immediately
    }

    if( andHandleNow ) { // End of the chain, do what is needed
      handleErrorsInQueue();
    }

    if( andStopHere ) { // leave error queued, but let normal progress resume
      return convertErrorTo; // return stops it being an error
    }
    throw error; // toss it to the next catch() clause
  };
};

/** Syntactic sugar around treatErrorAs() for the case of logging an error outside of a catch handler. Some default
 *  values are assumed for params that are different than calling treatErrorAs() - see comments below.
 *  @param treatErrorAsParams Passed through to treatErrorAs().
 */
export const logError = (treatErrorAsParams) => {
  const params = {
    // Some convenient defaults based on the idea that calling code is aware of an error and directly handling it.
    andStopHere:true, // Don't rethrow an exception.
    severity:errorLevel.INFO, // Information only--no implicit UI handling. "Severity" may be a bit of misnomer here, since it is currently coupled to how the UI handles an error.
    andHandleNow:true, // Default to posting/logging immediately. Makes more sense with messages that don't have UI handling.
    ...treatErrorAsParams
  };
  const callback = treatErrorAs(params);
  const error = makeError(params);
  callback(error);
};

export const registerError = ({ severity, message, messageKey, actions, displayErrorAs}) => {
  const error = makeError({ error: new Error(message), severity, message, messageKey, actions, displayErrorAs});
  addToErrorQueue(error);
};

export const unitTestPrivate = {
  _getDefaultErrorMessageKey
};
